import { useOpenModal } from '@flowus/common/next-modal';
import { SearchParams } from '@flowus/login/const';
import { CollectionViewType } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import { Icon } from 'src/common/components/icon';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { listViewNormalClassName } from 'src/common/components/list-view/helper';
import { Tooltip } from 'src/common/components/tooltip';
import { SHARE_LINK, UNTITLED } from 'src/common/const';
import { segmentsToText } from 'src/editor/utils/editor';
import { useCollectionView } from 'src/hooks/collection-view/use-collection-view';
import { usePayInfo } from 'src/hooks/page/use-pay-info';
import { useCurrentUser } from 'src/hooks/user/use-current-user';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { usePickBlock } from 'src/utils/pick-block';
import { useOpenLogin } from '../empty/open-login';
import { useOpenWeappQR } from '../weapp-qr';
import { useGetSharePageUrl } from './helper';
import { useOpenShareLinkQr } from './share-link-qr';

interface ShareButtonsProps {
  uuid: string;
  url: string;
  onCopyLink?: () => void;
  onCopyEmbedLink?: () => void;
}
export const ShareButtons: FC<ShareButtonsProps> = (props) => {
  const { url, onCopyLink, uuid, onCopyEmbedLink } = props;
  const block = usePickBlock(uuid, ['data'], ['segments']);
  const collectionView = useCollectionView(uuid);
  const openWeappQR = useOpenWeappQR();
  const openShareLinkQr = useOpenShareLinkQr();

  let title = `分享了${segmentsToText(block?.data.segments) ?? UNTITLED}`;
  if (collectionView?.type === CollectionViewType.FORM) {
    title = '邀请你填写收集表';
  }

  return (
    <div className="px-4 border-t border-grey6 text-t2 bg-grey9 flex items-center text-active_color h-12">
      <span className="mr-4 text-black">分享方式</span>
      <Tooltip popup={SHARE_LINK} className="animate-click mr-4">
        <Icon name="IcUrl" size="middle" onClick={onCopyLink} />
      </Tooltip>
      {onCopyEmbedLink && (
        <Tooltip popup={'内嵌模式分享'} className="animate-click mr-4">
          <Icon name="IcUploadWeb" size="middle" onClick={onCopyEmbedLink} />
        </Tooltip>
      )}
      {/* {!(__BUILD_IN__ || __PRIVATE__) && (
        <Tooltip
          popup="小程序分享"
          onClick={() => openWeappQR({ url })}
          className="animate-click mr-4"
        >
          <Icon name="IcMenuMiniProgramQrcode" size="middle" />
        </Tooltip>
      )} */}
      <Tooltip
        popup="二维码分享"
        className="animate-click"
        onClick={() => openShareLinkQr({ url, title })}
      >
        <Icon name="IcMenuQrcode" size="middle" />
      </Tooltip>
    </div>
  );
};

export const useOpenShareLinkList = (uuid: string) => {
  const { payInfo } = usePayInfo(uuid);
  const openLogin = useOpenLogin();
  const isLogin = useIsLogin();
  const openModal = useOpenModal();
  const currentUser = useCurrentUser();
  const getSharePageUrl = useGetSharePageUrl(uuid);
  const { url, textAndUrl } = getSharePageUrl({ inviteCode: currentUser.inviteCode });
  const openWeappQR = useOpenWeappQR();
  const openShareLinkQr = useOpenShareLinkQr();
  const title = `分享了${payInfo?.publishAccessPageTitle || UNTITLED}`;

  const onCopy = useCallback(() => {
    void writeTextInClipboard(textAndUrl);
  }, [textAndUrl]);

  const copyEmbedLink = useCallback(async () => {
    const urlObj = new URL(url);
    urlObj.searchParams.set(SearchParams.embed, 'true');
    await writeTextInClipboard(urlObj.href, { message: '已复制链接' });
  }, [url]);

  return useCallback(
    async (event: MouseEvent) => {
      if (!isLogin) {
        openLogin();
        return;
      }

      openModal.dropdown({
        popcorn: event?.currentTarget,
        placement: 'bottom',
        content: (_) => (
          <ListView
            className={listViewNormalClassName}
            onItemClick={() => _.onCloseModal()}
            customHeader={
              <div className="px-4 text-grey3 text-t3 py-0.5">
                {payInfo?.sharePageDistributionRatio
                  ? '分享赚取订阅佣金和会员佣金'
                  : '分享赚取会员佣金'}
              </div>
            }
            items={[
              {
                type: ListItemType.OPERATION,
                data: {
                  title: '链接分享',
                  icon: 'IcUrl',
                  onClick: onCopy,
                },
              },
              {
                type: ListItemType.OPERATION,
                data: {
                  title: '内嵌模式分享',
                  icon: 'IcUploadWeb',
                  onClick: copyEmbedLink,
                },
              },
              // {
              //   type: ListItemType.OPERATION,
              //   data: {
              //     title: '小程序分享',
              //     icon: 'IcMenuMiniProgramQrcode',
              //     onClick: () => openWeappQR({ url }),
              //   },
              // },
              {
                type: ListItemType.OPERATION,
                data: {
                  title: '二维码分享',
                  icon: 'IcMenuQrcode',
                  onClick: () =>
                    openShareLinkQr({
                      url,
                      title,
                    }),
                },
              },
            ]}
          />
        ),
      });
    },
    [
      copyEmbedLink,
      isLogin,
      onCopy,
      openLogin,
      openModal,
      openShareLinkQr,
      payInfo?.sharePageDistributionRatio,
      title,
      url,
    ]
  );
};
